import { Dispatch, ReactNode, createContext, useCallback, useContext, useReducer } from 'react';

import { SimpleSnackbar, SimpleSnackbarProps } from 'SimpleSnackbar';

export interface SnackbarProps extends Omit<SimpleSnackbarProps, 'message'> {}
type SnackAction =
  | { type: 'enqueue'; payload?: SnackbarProps; message: string | ReactNode }
  | { type: 'dequeue'; key?: any };

type SnackState = {
  queue: (SimpleSnackbarProps | ReactNode)[];
};

const anchorOrigin = {
  vertical: 'bottom' as const,
  horizontal: 'center' as const,
};

export const snackbarReducer = (state: SnackState, action: SnackAction) => {
  switch (action.type) {
    case 'enqueue': {
      if (!action.payload) {
        console.error(
          'You invoked `enqueSnackbar` without passing a payload. No snackbar was added to the queue.',
        );
        return state;
      }

      // If a consumer is passing in a custom Snackbar as a `content` function, just throw it into the queue and render it
      if (action.payload.content)
        return {
          queue: [...state.queue, action.payload.content()],
        };
      let key = action.payload.key;
      let message = action.message;

      if (key == undefined) key = `${Date.now()}-${Math.random()}`;
      let newSnack = {
        ...action.payload,
        anchorOrigin: action.payload.anchorOrigin ?? anchorOrigin,
      };

      return {
        queue: [...state.queue, <SimpleSnackbar key={key} message={message} {...newSnack} />],
      };
    }

    case 'dequeue': {
      if (state.queue.length === 0) return state;
      if (action.key) {
        const newQueue = state.queue.filter((snack: SimpleSnackbarProps) => snack.key !== action.key);

        return {
          queue: newQueue,
        };
      }
      const newQueue = state.queue;
      newQueue.shift();
      return {
        queue: newQueue,
      };
    }

    default: {
      throw Error(
        `Unknown action type: ${action['type']}. Make sure to pass an action type that the snackbarReducer recognizes.`,
      );
    }
  }
};

type SnackContextType = {
  state: SnackState;
  dispatch?: Dispatch<SnackAction>;
};

export const SnackbarContext = createContext<SnackContextType>({
  state: { queue: [] },
});

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(snackbarReducer, {
    queue: [],
  });

  return (
    <SnackbarContext.Provider value={{ state, dispatch }}>
      {children}
      {state?.queue[0]}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const { dispatch } = useContext(SnackbarContext);

  if (!dispatch) {
    throw Error(
      '`useSnackbar` was invoked inside a component that was not wrapped within a SnackbarProvider. Review your component tree and make sure a SnackbarProvider exists near the root, then make sure that this component exists as a child of that Provider.',
    );
  }

  const enqueueSnackbar = useCallback(
    (message: string | ReactNode, payload?: SnackbarProps) => {
      dispatch({ type: 'enqueue', message: message, payload: { ...payload } });
    },
    [dispatch],
  );
  const closeSnackbar = useCallback(
    (key: any) => {
      // we wait 200ms to allow for the leaveDuration (195ms) to finish, otherwise the snackbar disappearing animation looks janky https://mui.com/customization/transitions/#durations
      setTimeout(() => {
        dispatch({ type: 'dequeue', key: key.toString() });
      }, 200);
    },
    [dispatch],
  );
  return { enqueueSnackbar, closeSnackbar };
};
